import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Phone,
  Mail,
  MapPin,
  GraduationCap,
  Shield,
  Copy,
  ExternalLink,
  Edit,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  formatDate,
  formatPhone,
  formatDOB,
  getStatusColor,
} from "../../../utils/clientHelpers";

export default function StudentViewModal({ isOpen, student, onClose, onEdit }) {
  if (!isOpen || !student) return null;

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    toast.success("Copied to clipboard!");
  };

  const cardFront = student.studentCardFront ?? student.card_front;
  const cardBack = student.studentCardBack ?? student.card_back;
  const dobDisplay =
    student.dob?.length === 8
      ? formatDOB(student.dob)
      : student.dob
        ? formatDate(student.dob)
        : "N/A";

  return (
    <AnimatePresence>
      {isOpen && student && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 backdrop-blur-sm bg-black/60"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="sticky top-0 z-10 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-center items-center w-12 h-12 rounded-full backdrop-blur-sm bg-white/20">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Student Details</h2>
                      <p className="text-sm text-blue-100">Complete information</p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 text-white rounded-lg transition-colors hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="p-6 space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200"
                  >
                    <div className="flex items-center mb-4">
                      <User className="mr-2 w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-bold text-blue-900">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Full Name</label>
                        <div className="flex justify-between items-center p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-semibold text-gray-900">
                            {student.firstName} {student.lastName}
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => copyToClipboard(`${student.firstName} ${student.lastName}`)}
                            className="p-1 text-blue-600 rounded hover:bg-blue-50"
                          >
                            <Copy className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Gender</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {student.gender || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Date of Birth</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">{dobDisplay}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Status</label>
                        <div className="mt-1">
                          <span
                            className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full border-2 ${getStatusColor(student.status)}`}
                          >
                            {student.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200"
                  >
                    <div className="flex items-center mb-4">
                      <Phone className="mr-2 w-5 h-5 text-purple-600" />
                      <h3 className="text-lg font-bold text-purple-900">Contact Information</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-purple-700 uppercase">Guardian Email</label>
                        <div className="flex justify-between items-center p-3 mt-1 bg-white rounded-lg">
                          <div className="flex flex-1 items-center min-w-0">
                            <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-purple-500" />
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {student.email || "N/A"}
                            </p>
                          </div>
                          {student.email && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(student.email)}
                              className="flex-shrink-0 p-1 ml-2 text-purple-600 rounded hover:bg-purple-50"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-purple-700 uppercase">Guardian Phone</label>
                        <div className="flex justify-between items-center p-3 mt-1 bg-white rounded-lg">
                          <div className="flex flex-1 items-center min-w-0">
                            <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-purple-500" />
                            <p className="text-sm font-medium text-gray-900">
                              {formatPhone(student.phone || student.phone_number)}
                            </p>
                          </div>
                          {(student.phone || student.phone_number) && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(student.phone || student.phone_number)}
                              className="flex-shrink-0 p-1 ml-2 text-purple-600 rounded hover:bg-purple-50"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200"
                  >
                    <div className="flex items-center mb-4">
                      <MapPin className="mr-2 w-5 h-5 text-emerald-600" />
                      <h3 className="text-lg font-bold text-emerald-900">Location</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-emerald-700 uppercase">Region</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">{student.region || "N/A"}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-emerald-700 uppercase">Location</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">{student.location || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200"
                  >
                    <div className="flex items-center mb-4">
                      <GraduationCap className="mr-2 w-5 h-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Student Information</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-amber-700 uppercase">School</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {student.schoolName || student.school_name || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-amber-700 uppercase">Student ID</label>
                        <div className="flex justify-between items-center p-3 mt-1 bg-white rounded-lg">
                          <p className="font-mono text-sm font-medium text-gray-900">
                            {student.studentId || student.student_school_id || "N/A"}
                          </p>
                          {(student.studentId || student.student_school_id) && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() =>
                                copyToClipboard(student.studentId || student.student_school_id)
                              }
                              className="p-1 ml-2 text-amber-600 rounded hover:bg-amber-50"
                            >
                              <Copy className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-amber-700 uppercase">Card Issue Date</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(
                              student.studentCardIssueDate || student.card_issue_date
                            )}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-amber-700 uppercase">Card Expiry Date</label>
                        <div className="p-3 mt-1 bg-white rounded-lg">
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(
                              student.studentCardExpiryDate || student.card_expiry_date
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    {(cardFront || cardBack) && (
                      <div>
                        <label className="block mb-2 text-xs font-semibold tracking-wide text-amber-700 uppercase">
                          Student Card Images
                        </label>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {cardFront && (
                            <div className="relative group">
                              <div className="flex absolute inset-0 z-10 justify-center items-end p-2 bg-gradient-to-t to-transparent rounded-lg opacity-0 transition-opacity from-black/50 group-hover:opacity-100">
                                <motion.a
                                  href={cardFront}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-gray-900 flex items-center"
                                >
                                  <ExternalLink className="mr-1 w-3 h-3" />
                                  View Full Size
                                </motion.a>
                              </div>
                              <img
                                src={cardFront}
                                alt="Student card front"
                                className="object-cover w-full h-48 rounded-lg border-2 border-amber-200 shadow-md"
                              />
                              <p className="mt-1 text-xs font-medium text-center text-amber-700">Front</p>
                            </div>
                          )}
                          {cardBack && (
                            <div className="relative group">
                              <div className="flex absolute inset-0 z-10 justify-center items-end p-2 bg-gradient-to-t to-transparent rounded-lg opacity-0 transition-opacity from-black/50 group-hover:opacity-100">
                                <motion.a
                                  href={cardBack}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-gray-900 flex items-center"
                                >
                                  <ExternalLink className="mr-1 w-3 h-3" />
                                  View Full Size
                                </motion.a>
                              </div>
                              <img
                                src={cardBack}
                                alt="Student card back"
                                className="object-cover w-full h-48 rounded-lg border-2 border-amber-200 shadow-md"
                              />
                              <p className="mt-1 text-xs font-medium text-center text-amber-700">Back</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
                  >
                    <div className="flex items-center mb-4">
                      <Shield className="mr-2 w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-bold text-gray-900">Additional Information</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-semibold tracking-wide text-gray-700 uppercase">Student TripSecure ID</label>
                        <div className="flex justify-between items-center p-3 mt-1 bg-white rounded-lg">
                          <p className="font-mono text-xs text-gray-600 truncate">
                            {student.slug || "N/A"}
                          </p>
                          {student.slug && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => copyToClipboard(student.slug)}
                              className="flex-shrink-0 p-1 ml-2 text-gray-600 rounded hover:bg-gray-50"
                            >
                              <Copy className="w-3 h-3" />
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              <div className="flex sticky bottom-0 gap-3 justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Close
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onClose();
                    onEdit?.(student);
                  }}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:from-emerald-700 hover:to-teal-700 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Student
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
